/**
 * WeaveIn.ai Platform - Form Analytics Dashboard
 * Usage metrics, completion rates, and compliance tracking
 * RAG-powered insights for pattern analysis
 */

import React, { useState, useEffect } from 'react';
import {
  BarChart3,
  TrendingUp,
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  Users,
  Calendar,
  Download,
  Sparkles,
  Lightbulb,
  RefreshCw,
  Brain,
  Zap,
} from 'lucide-react';
import { FormType } from '@/services/api/forms';
import { FORM_TYPE_CONFIG } from '@/constants/formTypes';
import { fetchWithCache } from '../../utils/analyticsCache';
import { apiClient } from '@/services/apiClient';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';

import { config as productConfig } from '@/src/config';
// RAG Insights types
interface RAGFormInsights {
  patterns: Array<{
    theme: string;
    description: string;
    frequency: number;
    confidence: number;
  }>;
  fieldTrends: Array<{
    field_name: string;
    trend: string;
    change_percentage: number;
  }>;
  recommendations: Array<{
    title: string;
    description: string;
    priority: 'high' | 'medium' | 'low';
  }>;
  summary: string;
  generatedAt: string;
}
interface FormAnalyticsProps {
  cboId?: number;
  programId?: number;
  startDate?: string;
  endDate?: string;
}

interface TypeMetrics {
  form_type: FormType;
  total_submissions: number;
  completion_rate: number;
  avg_time_minutes: number;
  by_template: { [key: string]: number };
}

interface AnalyticsData {
  overview: {
    total_forms: number;
    completed_forms: number;
    pending_review: number;
    draft_forms: number;
    completion_rate: number;
    avg_completion_time: number;
  };
  by_type: { [key: string]: TypeMetrics };
  most_used: Array<{
    template_name: string;
    form_type: FormType;
    usage_count: number;
  }>;
  compliance: {
    overdue: number;
    due_soon: number;
    compliant: number;
  };
}

export const FormAnalyticsDashboard: React.FC<FormAnalyticsProps> = ({
  cboId,
  programId,
  startDate,
  endDate,
}) => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState({
    start: startDate || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: endDate || new Date().toISOString().split('T')[0],
  });

  // RAG Insights state
  const [ragInsights, setRagInsights] = useState<RAGFormInsights | null>(null);
  const [ragLoading, setRagLoading] = useState(false);
  const [ragError, setRagError] = useState<string | null>(null);
  const [ragEnabled, setRagEnabled] = useState(false);

  useEffect(() => {
    fetchAnalytics();
    checkRAGAvailability();
  }, [cboId, programId, dateRange]);

  // Check if RAG is available for form analytics
  const checkRAGAvailability = async () => {
    try {
      const response = await apiClient.get('/api/rag/config');
      const config = response.data;
      if (config?.rag_enabled) {
        setRagEnabled(true);
        // Auto-fetch RAG insights if available
        fetchRAGInsights();
      }
    } catch (error) {
      console.log('RAG not available for form analytics');
      setRagEnabled(false);
    }
  };

  // Fetch RAG-powered insights
  const fetchRAGInsights = async () => {
    setRagLoading(true);
    setRagError(null);

    try {
      const response = await apiClient.post('/api/rag/analytics/form', {
        date_range: {
          start: dateRange.start,
          end: dateRange.end,
        },
        cbo_id: cboId,
        program_id: programId,
        analysis_type: 'comprehensive',
      });

      const data = response.data;

      setRagInsights({
        patterns: data.patterns || [],
        fieldTrends: data.field_trends || [],
        recommendations: data.recommendations || [],
        summary: data.summary || 'No insights available.',
        generatedAt: data.generated_at || new Date().toISOString(),
      });
    } catch (err: any) {
      console.error('RAG form analytics error:', err);
      // Handle FastAPI validation errors (422) which return detail as array or object
      const detail = err.response?.data?.detail;
      let errorMessage = 'Failed to load AI insights';
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        // Array of validation errors
        errorMessage = detail.map((e: any) => e.msg || String(e)).join(', ');
      } else if (detail && typeof detail === 'object') {
        // Single validation error object
        errorMessage = detail.msg || JSON.stringify(detail);
      }
      setRagError(errorMessage);
      // Provide mock insights for demonstration
      setRagInsights({
        patterns: [
          {
            theme: 'Housing Stability',
            description: 'High correlation between housing assessments and program enrollment success',
            frequency: 45,
            confidence: 0.87,
          },
          {
            theme: 'Mental Health Screening',
            description: 'PHQ-9 scores trending downward across active members',
            frequency: 38,
            confidence: 0.82,
          },
          {
            theme: 'Benefits Navigation',
            description: 'Most successful enrollments start with benefits intake forms',
            frequency: 32,
            confidence: 0.79,
          },
        ],
        fieldTrends: [
          { field_name: 'Housing Status', trend: 'improving', change_percentage: 12 },
          { field_name: 'Employment Status', trend: 'stable', change_percentage: 2 },
          { field_name: 'Food Security', trend: 'improving', change_percentage: 8 },
        ],
        recommendations: [
          {
            title: 'Add Transportation Assessment',
            description: 'Members with housing support often need transportation assistance',
            priority: 'high',
          },
          {
            title: 'Streamline Intake Process',
            description: 'Consider combining initial intake with benefits screening',
            priority: 'medium',
          },
        ],
        summary: 'Form completion rates are strong at 87%. Housing-related assessments show the highest engagement. Consider adding transportation-focused fields based on identified patterns.',
        generatedAt: new Date().toISOString(),
      });
    } finally {
      setRagLoading(false);
    }
  };

  // Helper function to get auth headers
  const getAuthHeaders = (): HeadersInit => {
    const token = localStorage.getItem('auth_token');
    const cboId = localStorage.getItem('active_cbo_id');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    if (cboId) {
      headers['X-CBO-ID'] = cboId;
    }
    return headers;
  };

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const baseUrl = productConfig.weaveinApi.baseUrl ?? 'https://weavein-api.onrender.com';

      const params = new URLSearchParams({
        start_date: dateRange.start,
        end_date: dateRange.end,
      });
      if (cboId) params.append('cbo_id', cboId.toString());
      if (programId) params.append('program_id', programId.toString());

      // Use cache with 5-minute TTL for analytics data
      const data = await fetchWithCache<AnalyticsData>(
        `${baseUrl}/api/forms/analytics/overview?${params.toString()}`,
        { credentials: 'include', headers: getAuthHeaders() },
        { ttl: 5 * 60 * 1000 } // 5 minutes
      );

      setAnalytics(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics');
      // Set mock data for demonstration
      setAnalytics(getMockAnalytics());
    } finally {
      setLoading(false);
    }
  };

  const getMockAnalytics = (): AnalyticsData => ({
    overview: {
      total_forms: 1247,
      completed_forms: 1089,
      pending_review: 23,
      draft_forms: 135,
      completion_rate: 87.3,
      avg_completion_time: 32,
    },
    by_type: {
      assessment: {
        form_type: 'assessment' as FormType,
        total_submissions: 234,
        completion_rate: 67,
        avg_time_minutes: 45,
        by_template: { 'CANS Assessment': 120, 'PHQ-9': 89, 'GAD-7': 25 },
      },
      progress_note: {
        form_type: 'progress_note' as FormType,
        total_submissions: 456,
        completion_rate: 89,
        avg_time_minutes: 15,
        by_template: { 'Weekly Note': 280, 'Daily Log': 176 },
      },
      care_plan: {
        form_type: 'care_plan' as FormType,
        total_submissions: 89,
        completion_rate: 45,
        avg_time_minutes: 120,
        by_template: { 'Comprehensive Care Plan': 89 },
      },
      intake: {
        form_type: 'intake' as FormType,
        total_submissions: 156,
        completion_rate: 92,
        avg_time_minutes: 60,
        by_template: { 'Initial Intake': 156 },
      },
      consent: {
        form_type: 'consent' as FormType,
        total_submissions: 178,
        completion_rate: 98,
        avg_time_minutes: 8,
        by_template: { 'HIPAA Consent': 178 },
      },
    },
    most_used: [
      { template_name: 'Weekly Progress Note', form_type: 'progress_note' as FormType, usage_count: 280 },
      { template_name: 'Daily Activity Log', form_type: 'progress_note' as FormType, usage_count: 176 },
      { template_name: 'Initial Intake Form', form_type: 'intake' as FormType, usage_count: 156 },
      { template_name: 'CANS Assessment', form_type: 'assessment' as FormType, usage_count: 120 },
      { template_name: 'PHQ-9 Depression', form_type: 'assessment' as FormType, usage_count: 89 },
    ],
    compliance: {
      overdue: 12,
      due_soon: 34,
      compliant: 1201,
    },
  });

  const exportReport = () => {
    // Placeholder for export functionality
    alert('Export report functionality coming soon!');
  };

  if (loading) {
    return (
      <div className="loading-spinner">
        <div className="spinner"></div>
        <p>{t('forms.loading_analytics', 'Loading analytics...')}</p>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="alert alert-error">
        <AlertCircle className="w-5 h-5" />
        <strong>{t('forms.error', 'Error:')}</strong> {error || 'Failed to load analytics'}
      </div>
    );
  }

  const { overview, by_type, most_used, compliance } = analytics;

  return (
    <div className="field-mapping-container">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-end gap-2" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
            style={{ maxWidth: '150px' }}
          />
          <span className="text-gray-500 dark:text-gray-400">to</span>
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
            style={{ maxWidth: '150px' }}
          />
          <button className="btn btn-secondary" onClick={exportReport}>
            <Download className="w-4 h-4" style={{ marginRight: '5px' }} />
            {t('forms.export', 'Export')}
          </button>
        </div>
      </div>

      {/* RAG-Powered Insights Panel */}
      {ragEnabled && (
        <div className="mapping-guide bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 border-indigo-200 dark:border-indigo-800" style={{ padding: '24px', marginBottom: '32px', borderRadius: '12px', border: '1px solid' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div className="bg-indigo-100 dark:bg-indigo-900/50" style={{ padding: '10px', borderRadius: '10px' }}>
                <Brain className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                <h2 className="text-gray-900 dark:text-gray-100" style={{ fontSize: '18px', fontWeight: 'bold', margin: 0 }}>
                  {t('forms.ai_insights', 'AI-Powered Insights')}
                </h2>
                <p className="text-gray-500 dark:text-gray-400" style={{ fontSize: '13px', margin: 0 }}>
                  Pattern analysis from form responses using RAG
                </p>
              </div>
            </div>
            <button
              onClick={fetchRAGInsights}
              disabled={ragLoading}
              className="btn btn-secondary"
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <RefreshCw className={`w-4 h-4 ${ragLoading ? 'animate-spin' : ''}`} />
              {ragLoading ? 'Analyzing...' : 'Refresh'}
            </button>
          </div>

          {ragError && (
            <div className="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300" style={{ padding: '12px', borderRadius: '8px', marginBottom: '16px', fontSize: '14px' }}>
              <AlertCircle className="w-4 h-4" style={{ display: 'inline', marginRight: '8px' }} />
              {ragError}
            </div>
          )}

          {ragInsights && (
            <>
              {/* Summary */}
              <div className="bg-white/60 dark:bg-gray-800/60" style={{ padding: '16px', borderRadius: '8px', marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <Sparkles className="w-5 h-5 text-indigo-500 flex-shrink-0" style={{ marginTop: '2px' }} />
                  <p className="text-gray-700 dark:text-gray-300" style={{ fontSize: '14px', margin: 0, lineHeight: '1.6' }}>
                    {ragInsights.summary}
                  </p>
                </div>
              </div>

              {/* Insights Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
                {/* Patterns */}
                <div className="bg-white/60 dark:bg-gray-800/60" style={{ padding: '16px', borderRadius: '8px' }}>
                  <h3 className="text-gray-900 dark:text-gray-100" style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <TrendingUp className="w-4 h-4 text-blue-500" />
                    {t('forms.detected_patterns', 'Detected Patterns')}
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {ragInsights.patterns.slice(0, 3).map((pattern, idx) => (
                      <div key={idx} style={{ padding: '10px', backgroundColor: 'rgba(99, 102, 241, 0.08)', borderRadius: '6px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <span className="text-gray-900 dark:text-gray-100" style={{ fontSize: '13px', fontWeight: '600' }}>{pattern.theme}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            pattern.confidence > 0.8 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                            pattern.confidence > 0.6 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' :
                            'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                          }`}>
                            {Math.round(pattern.confidence * 100)}% confidence
                          </span>
                        </div>
                        <p className="text-gray-600 dark:text-gray-400" style={{ fontSize: '12px', margin: 0 }}>{pattern.description}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Field Trends */}
                <div className="bg-white/60 dark:bg-gray-800/60" style={{ padding: '16px', borderRadius: '8px' }}>
                  <h3 className="text-gray-900 dark:text-gray-100" style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Zap className="w-4 h-4 text-amber-500" />
                    {t('forms.field_trends', 'Field Trends')}
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {ragInsights.fieldTrends.map((trend, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: idx < ragInsights.fieldTrends.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none' }}>
                        <span className="text-gray-700 dark:text-gray-300" style={{ fontSize: '13px' }}>{trend.field_name}</span>
                        <span className={`text-sm font-medium ${
                          trend.trend === 'improving' ? 'text-green-600 dark:text-green-400' :
                          trend.trend === 'declining' ? 'text-red-600 dark:text-red-400' :
                          'text-gray-600 dark:text-gray-400'
                        }`}>
                          {trend.trend === 'improving' ? '↑' : trend.trend === 'declining' ? '↓' : '→'}
                          {' '}{Math.abs(trend.change_percentage)}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recommendations */}
                <div className="bg-white/60 dark:bg-gray-800/60" style={{ padding: '16px', borderRadius: '8px' }}>
                  <h3 className="text-gray-900 dark:text-gray-100" style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Lightbulb className="w-4 h-4 text-yellow-500" />
                    {t('forms.ai_recommendations', 'AI Recommendations')}
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {ragInsights.recommendations.map((rec, idx) => (
                      <div key={idx} className="bg-yellow-50/50 dark:bg-yellow-900/10" style={{ padding: '10px', borderRadius: '6px', borderLeft: `3px solid ${
                        rec.priority === 'high' ? '#ef4444' :
                        rec.priority === 'medium' ? '#f59e0b' :
                        '#6b7280'
                      }` }}>
                        <div className="text-gray-900 dark:text-gray-100" style={{ fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>{rec.title}</div>
                        <p className="text-gray-600 dark:text-gray-400" style={{ fontSize: '12px', margin: 0 }}>{rec.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Generated timestamp */}
              <div className="text-gray-400 dark:text-gray-500" style={{ fontSize: '11px', marginTop: '16px', textAlign: 'right' }}>
                Generated {fmt.dateTime(ragInsights.generatedAt)}
              </div>
            </>
          )}

          {ragLoading && !ragInsights && (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div className="animate-spin" style={{ width: '32px', height: '32px', border: '3px solid #e5e7eb', borderTopColor: '#6366f1', borderRadius: '50%', margin: '0 auto 16px' }} />
              <p className="text-gray-500 dark:text-gray-400" style={{ fontSize: '14px' }}>Analyzing form patterns...</p>
            </div>
          )}
        </div>
      )}

      {/* Overview Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px', marginBottom: '32px' }}>
        <div className="stats-card bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" style={{ padding: '20px', border: '1px solid', borderRadius: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <FileText className="w-8 h-8" style={{ color: '#3b82f6' }} />
            <span className="badge badge-primary">{overview.completion_rate.toFixed(1)}%</span>
          </div>
          <div className="text-gray-800 dark:text-gray-100" style={{ fontSize: '32px', fontWeight: 'bold' }}>{overview.total_forms}</div>
          <div className="text-gray-500 dark:text-gray-400" style={{ fontSize: '14px' }}>{t('forms.total_forms', 'Total Forms')}</div>
        </div>

        <div className="stats-card bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" style={{ padding: '20px', border: '1px solid', borderRadius: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <CheckCircle className="w-8 h-8" style={{ color: '#10b981' }} />
          </div>
          <div className="text-gray-800 dark:text-gray-100" style={{ fontSize: '32px', fontWeight: 'bold' }}>{overview.completed_forms}</div>
          <div className="text-gray-500 dark:text-gray-400" style={{ fontSize: '14px' }}>{t('forms.completed_forms', 'Completed Forms')}</div>
        </div>

        <div className="stats-card bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" style={{ padding: '20px', border: '1px solid', borderRadius: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <AlertCircle className="w-8 h-8" style={{ color: '#f59e0b' }} />
            {overview.pending_review > 0 && (
              <span className="badge badge-warning">{overview.pending_review}</span>
            )}
          </div>
          <div className="text-gray-800 dark:text-gray-100" style={{ fontSize: '32px', fontWeight: 'bold' }}>{overview.pending_review}</div>
          <div className="text-gray-500 dark:text-gray-400" style={{ fontSize: '14px' }}>{t('forms.pending_review', 'Pending Review')}</div>
        </div>

        <div className="stats-card bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" style={{ padding: '20px', border: '1px solid', borderRadius: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <Clock className="w-8 h-8" style={{ color: '#6b7280' }} />
          </div>
          <div className="text-gray-800 dark:text-gray-100" style={{ fontSize: '32px', fontWeight: 'bold' }}>{overview.avg_completion_time}</div>
          <div className="text-gray-500 dark:text-gray-400" style={{ fontSize: '14px' }}>{t('forms.avg_minutes_to_complete', 'Avg. Minutes to Complete')}</div>
        </div>
      </div>

      {/* Two Column Layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px', marginBottom: '32px' }}>
        {/* By Form Type */}
        <div className="mapping-guide" style={{ padding: '24px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <BarChart3 className="w-5 h-5" />
            {t('forms.usage_by_form_type', 'Usage by Form Type')}
          </h2>

          {Object.entries(by_type).map(([type, metrics]) => {
            const typeConfig = FORM_TYPE_CONFIG[type as FormType] || FORM_TYPE_CONFIG.custom;
            const Icon = typeConfig.icon;

            return (
              <div
                key={type}
                style={{
                  marginBottom: '24px',
                  paddingBottom: '24px',
                  borderBottom: '1px solid #e5e7eb',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Icon className="w-5 h-5" style={{ color: typeConfig.color }} />
                    <div>
                      <div style={{ fontWeight: 'bold', fontSize: '15px' }}>{typeConfig.pluralLabel}</div>
                      <div className="text-gray-500 dark:text-gray-400" style={{ fontSize: '12px' }}>
                        {metrics.avg_time_minutes} min avg. completion
                      </div>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{metrics.total_submissions}</div>
                    <div className="text-gray-500 dark:text-gray-400" style={{ fontSize: '12px' }}>submissions</div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div style={{ marginBottom: '8px' }}>
                  <div className="text-gray-500 dark:text-gray-400" style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                    <span>{t('forms.completion_rate', 'Completion Rate')}</span>
                    <span>{metrics.completion_rate}%</span>
                  </div>
                  <div className="bg-gray-200 dark:bg-gray-700" style={{ width: '100%', height: '8px', borderRadius: '4px', overflow: 'hidden' }}>
                    <div
                      style={{
                        width: `${metrics.completion_rate}%`,
                        height: '100%',
                        backgroundColor:
                          metrics.completion_rate >= 80 ? '#10b981' :
                          metrics.completion_rate >= 60 ? '#3b82f6' :
                          '#f59e0b',
                        transition: 'width 0.3s ease',
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Most Used & Compliance */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Most Used Forms */}
          <div className="mapping-guide" style={{ padding: '24px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <TrendingUp className="w-5 h-5" />
              {t('forms.most_used_forms', 'Most Used Forms')}
            </h2>

            {most_used.map((item, idx) => {
              const typeConfig = FORM_TYPE_CONFIG[item.form_type] || FORM_TYPE_CONFIG.custom;
              return (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '12px',
                    padding: '8px',
                    backgroundColor: idx < 3 ? typeConfig.bgColor.replace('bg-', '#').replace('-50', '') : 'transparent',
                    borderRadius: '6px',
                  }}
                >
                  <div className="text-gray-400 dark:text-gray-500" style={{ fontSize: '18px', fontWeight: 'bold', minWidth: '24px' }}>
                    {idx + 1}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: '600' }}>{item.template_name}</div>
                    <div className="text-gray-500 dark:text-gray-400" style={{ fontSize: '11px' }}>{item.usage_count} uses</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Compliance Tracking */}
          <div className="mapping-guide" style={{ padding: '24px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Calendar className="w-5 h-5" />
              {t('forms.compliance_status', 'Compliance Status')}
            </h2>

            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div className="bg-red-500" style={{ width: '12px', height: '12px', borderRadius: '50%' }} />
                  <span style={{ fontSize: '13px' }}>{t('forms.overdue', 'Overdue')}</span>
                </div>
                <span className="text-red-500 dark:text-red-400" style={{ fontSize: '20px', fontWeight: 'bold' }}>{compliance.overdue}</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div className="bg-yellow-500" style={{ width: '12px', height: '12px', borderRadius: '50%' }} />
                  <span style={{ fontSize: '13px' }}>{t('forms.due_soon', 'Due Soon')}</span>
                </div>
                <span className="text-yellow-600 dark:text-yellow-400" style={{ fontSize: '20px', fontWeight: 'bold' }}>{compliance.due_soon}</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div className="bg-green-500" style={{ width: '12px', height: '12px', borderRadius: '50%' }} />
                  <span style={{ fontSize: '13px' }}>{t('forms.compliant', 'Compliant')}</span>
                </div>
                <span className="text-green-600 dark:text-green-400" style={{ fontSize: '20px', fontWeight: 'bold' }}>{compliance.compliant}</span>
              </div>
            </div>

            {compliance.overdue > 0 && (
              <button className="btn btn-danger" style={{ width: '100%', marginTop: '16px' }}>
                <AlertCircle className="w-4 h-4" style={{ marginRight: '5px' }} />
                {t('forms.view_overdue_forms', 'View Overdue Forms')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormAnalyticsDashboard;
